import React from 'react';
import { Redirect } from 'react-router';

import { RouterError } from '~src/bootstrap/routerHelpers/RouterError';
import { getFavoriteRedirect } from '~src/favorites/models/FavoriteRedirects';
import { Favorites } from '~src/favorites/resources/Favorites';
import { FavoriteItemType } from '~src/favorites/types/IFavorite';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

export interface RedirectToFavoritedContentProviderProps {
  readonly contentType: FavoriteItemType;
  // Undefined when the favorite's content is already known to be gone (see favoriteDatasourceDoesntExistPath).
  readonly id: string | undefined;
  readonly idForFavoriting: string;
  readonly urls: SpacesSiteUrls;
}

type ContentStatus = 'unfetched' | 'fetched' | 'error';

// Opens a favorite, or removes it and shows an error page when its content no longer exists.
export const RedirectToFavoritedContentProvider: React.FunctionComponent<RedirectToFavoritedContentProviderProps> = props => {
  const [ contentStatus, setContentStatus ] = React.useState<ContentStatus>('unfetched');

  React.useEffect(
    () => {
      let didCancel = false;

      const contentExists = props.id === undefined
        ? Promise.resolve(false)
        : getFavoriteRedirect(props.contentType).route.contentExists(props.id);
      contentExists
        .catch(() => false)
        .then(exists => {
          if (!exists) {
            Favorites.remove(props.idForFavoriting, props.contentType);
          }
          !didCancel && setContentStatus(exists ? 'fetched' : 'error');
        });

      return () => didCancel = true;
    },
    [props.contentType, props.id, props.idForFavoriting]
  );

  if (contentStatus === 'unfetched') {
    return null;
  } else if (contentStatus === 'fetched') {
    return <Redirect to={getFavoriteRedirect(props.contentType).route.contentPath(props.id, props.urls)}/>;
  } else {
    return <RouterError errorType={'removed-favorite-content'} contentType={props.contentType} urls={props.urls} />;
  }
};
